import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { formsAPI } from '../services/api';
import toast from 'react-hot-toast';

interface ServiceForm {
  id: number;
  customerName: string;
  customerId?: string;
  serviceLine: string;
  kitNumber?: string;
  oldServiceLine?: string;
  packageLimit: number;
  dataTopup?: number;
  oldPackage?: number;
  currentUsage: number;
  usagePercentage?: number;
  notified75?: boolean;
  notified100?: boolean;
  status: string;
  monitorStatus?: string;
  activationDate?: string;
  lastChecked: string;
  accountManager?: string;
  owner: {
    id: number;
    username: string;
    email: string;
  };
  isActive?: boolean;
}

const DataTransfer: React.FC = () => {
  const [customerIdSearch, setCustomerIdSearch] = useState('');
  const [sourceSearch, setSourceSearch] = useState('');
  const [destinationSearch, setDestinationSearch] = useState('');
  const [selectedSource, setSelectedSource] = useState<ServiceForm | null>(null);
  const [selectedDestination, setSelectedDestination] = useState<ServiceForm | null>(null);
  const [transferAmount, setTransferAmount] = useState<number>(0);
  const [customerForms, setCustomerForms] = useState<ServiceForm[]>([]);
  const debounce = useRef<NodeJS.Timeout>();
  const sourceSearchDebounce = useRef<NodeJS.Timeout>();
  const destinationSearchDebounce = useRef<NodeJS.Timeout>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Search for forms by customerId
  const { isLoading: isSearching, refetch: refetchSearch } = useQuery(
    ['forms-search', customerIdSearch],
    () => formsAPI.getAllForms({ search: customerIdSearch }),
    {
      enabled: customerIdSearch.length > 0,
      onSuccess: (data) => {
        // Handle both array and paginated response structures
        const formsArray: ServiceForm[] = Array.isArray(data)
          ? data
          : (data?.data && Array.isArray(data.data) ? data.data : []);
        
        // Filter to only show forms with matching customerId
        const filtered = formsArray.filter((form: ServiceForm) => 
          form.customerId?.toLowerCase() === customerIdSearch.toLowerCase()
        );
        setCustomerForms(filtered);
        // Reset selections when new search is performed
        setSelectedSource(null);
        setSelectedDestination(null);
        setTransferAmount(0);
      },
      onError: (error: any) => {
        console.error('Error searching forms:', error);
        toast.error('Failed to search forms');
      }
    }
  );

  // Debounced search
  useEffect(() => {
    if (debounce.current) {
      clearTimeout(debounce.current);
    }
    
    if (customerIdSearch.length > 0) {
      debounce.current = setTimeout(() => {
        refetchSearch();
      }, 500);
    } else {
      setCustomerForms([]);
      setSelectedSource(null);
      setSelectedDestination(null);
      setTransferAmount(0);
      setSourceSearch(''); // Clear source search when customer ID search is cleared
      setDestinationSearch(''); // Clear destination search when customer ID search is cleared
    }

    return () => {
      if (debounce.current) {
        clearTimeout(debounce.current);
      }
    };
  }, [customerIdSearch, refetchSearch]);

  // Cleanup search debounces on unmount
  useEffect(() => {
    return () => {
      if (sourceSearchDebounce.current) {
        clearTimeout(sourceSearchDebounce.current);
      }
      if (destinationSearchDebounce.current) {
        clearTimeout(destinationSearchDebounce.current);
      }
    };
  }, []);

  // Mutation for updating forms
  const updateFormMutation = useMutation(
    ({ id, data }: { id: string; data: any }) => formsAPI.updateForm(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['forms-search', customerIdSearch]);
        queryClient.invalidateQueries(['forms']);
      },
      onError: (error: any) => {
        const message = error.response?.data?.message || error.response?.data?.detail || 'Update failed';
        toast.error(message);
      }
    }
  );

  // Handle transfer
  const handleTransfer = async () => {
    if (!selectedSource || !selectedDestination) {
      toast.error('Please select both source and destination services');
      return;
    }

    if (transferAmount <= 0) {
      toast.error('Transfer amount must be greater than 0');
      return;
    }

    // Calculate available data (packageLimit + topup - currentUsage)
    const totalLimit = selectedSource.packageLimit + (selectedSource.dataTopup || 0);
    const available = totalLimit - selectedSource.currentUsage;
    const maxAvailable = Math.max(0, available);

    if (transferAmount > maxAvailable) {
      toast.error(`Transfer amount cannot exceed available data (${maxAvailable.toFixed(2)} GB)`);
      return;
    }

    if (selectedSource.id === selectedDestination.id) {
      toast.error('Source and destination cannot be the same service');
      return;
    }

    try {
      toast.loading('Processing transfer...', { id: 'transfer' });

      // Calculate new package limit (ensure source doesn't go below 0)
      const newSourceLimit = selectedSource.packageLimit - transferAmount;

      // Ensure source doesn't go below 0
      if (newSourceLimit < 0) {
        toast.error('Transfer amount would result in negative package limit', { id: 'transfer' });
        return;
      }

      // Use the new transfer endpoint
      await formsAPI.transferData(
        selectedSource.id,
        selectedDestination.id,
        transferAmount
      );

      toast.success(`Successfully transferred ${transferAmount} GB from ${selectedSource.serviceLine} to ${selectedDestination.serviceLine}`, { id: 'transfer' });
      
      // Reset selections
      setSelectedSource(null);
      setSelectedDestination(null);
      setTransferAmount(0);
      
      // Refresh the forms list
      refetchSearch();
    } catch (error: any) {
      toast.error('Transfer failed. Please try again.', { id: 'transfer' });
      console.error('Transfer error:', error);
    }
  };

  // Calculate available transfer amount (packageLimit + topup - currentUsage)
  const getMaxTransferAmount = () => {
    if (!selectedSource) return 0;
    const totalLimit = selectedSource.packageLimit + (selectedSource.dataTopup || 0);
    const available = totalLimit - selectedSource.currentUsage;
    return Math.max(0, available); // Ensure it's not negative
  };

  // Filter source services by search (serviceLine or kitNumber)
  const filteredSourceServices = customerForms.filter(form => {
    if (!sourceSearch) return true;
    const searchLower = sourceSearch.toLowerCase();
    const matchesServiceLine = form.serviceLine?.toLowerCase().includes(searchLower);
    const matchesKitNumber = form.kitNumber?.toLowerCase().includes(searchLower);
    return matchesServiceLine || matchesKitNumber;
  });

  // Filter destination services by search (serviceLine or kitNumber)
  const filteredDestinationServices = customerForms.filter(form => {
    if (!destinationSearch) return true;
    const searchLower = destinationSearch.toLowerCase();
    const matchesServiceLine = form.serviceLine?.toLowerCase().includes(searchLower);
    const matchesKitNumber = form.kitNumber?.toLowerCase().includes(searchLower);
    return matchesServiceLine || matchesKitNumber;
  });

  // Show all services in both panels, but exclude selected ones from the opposite panel
  const left = filteredSourceServices.filter(form => form.id !== selectedDestination?.id);  // Filtered services except selected destination
  const right = filteredDestinationServices.filter(form => form.id !== selectedSource?.id);     // Filtered services except selected source

  return (
    <div className="space-y-8">
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 p-8 text-white shadow-2xl">
        <div className="absolute inset-0 opacity-30 [background:radial-gradient(circle_at_15%_20%,rgba(56,189,248,0.35),transparent_45%),radial-gradient(circle_at_85%_25%,rgba(167,139,250,0.28),transparent_55%)]" />
        <div className="relative flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 backdrop-blur">
              <span className={`h-2 w-2 rounded-full ${customerForms.length > 0 ? 'bg-emerald-400' : 'bg-white/40'}`} />
              {customerForms.length > 0 ? `${customerForms.length} services loaded` : 'Search a customer to begin'}
            </div>
            <h1 className="text-3xl font-black tracking-tight sm:text-4xl">Data Transfer</h1>
            <p className="max-w-2xl text-white/70">
              Transfer package limits between two services for the same customer. Choose a source, destination, and amount.
            </p>
            <div className="flex flex-wrap items-center gap-3 text-sm text-white/70">
              <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <i className="fas fa-exchange-alt text-white/60"></i>
                Secure transfer flow
              </span>
              <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <i className="fas fa-clock text-white/60"></i>
                Updated: {new Date().toLocaleTimeString()}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate('/forms')}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white/10 px-5 py-3 font-semibold text-white shadow-lg transition-all hover:bg-white/15"
            >
              <i className="fas fa-arrow-left"></i>
              Back to Pool Customers
            </button>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="relative flex-1 max-w-md w-full">
            <input
              type="text"
              className="w-full rounded-2xl bg-gray-50 px-5 py-3 pl-12 text-gray-900 placeholder-gray-400 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              placeholder="Enter Customer ID to search..."
              value={customerIdSearch}
              onChange={(e) => setCustomerIdSearch(e.target.value)}
            />
            <i className="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
          </div>
          {isSearching && (
            <div className="flex items-center text-gray-600">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent mr-2"></div>
              <span>Searching...</span>
            </div>
          )}
          {!isSearching && customerIdSearch && (
            <button
              onClick={() => setCustomerIdSearch('')}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gray-100 px-4 py-3 font-semibold text-gray-800 hover:bg-gray-200 transition-all"
              title="Clear search"
            >
              <i className="fas fa-times"></i>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Transfer Controls */}
      {customerForms.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-black text-gray-900">Transfer configuration</h3>
              <p className="text-sm text-gray-600 mt-1">Review the source, amount, and destination before submitting.</p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
              <i className="fas fa-sliders-h"></i>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Source Service
              </label>
              <div className="p-4 bg-gray-50 rounded-2xl border border-gray-200">
                {selectedSource ? (
                  <div>
                    <div className="font-semibold text-gray-900">{selectedSource.serviceLine}</div>
                    <div className="text-sm text-gray-600">
                      Limit: {selectedSource.packageLimit} GB
                      {selectedSource.dataTopup && Number(selectedSource.dataTopup) > 0 && (
                        <span className="text-green-600"> + {selectedSource.dataTopup} GB Topup</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      Usage: {selectedSource.currentUsage.toFixed(2)} GB
                    </div>
                    <div className="text-sm font-semibold text-blue-600 mt-1">
                      Available: {getMaxTransferAmount().toFixed(2)} GB
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-400 italic">Select from left panel</div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Transfer Amount (GB)
              </label>
              <input
                type="number"
                min="0"
                max={getMaxTransferAmount()}
                step="0.1"
                value={transferAmount || ''}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  const maxAvailable = getMaxTransferAmount();
                  // Prevent entering more than available
                  if (value > maxAvailable) {
                    setTransferAmount(maxAvailable);
                    toast.error(`Cannot transfer more than available data (${maxAvailable.toFixed(2)} GB)`, { duration: 2000 });
                  } else {
                    setTransferAmount(value);
                  }
                }}
                className="w-full rounded-2xl bg-gray-50 px-4 py-3 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all duration-200"
                placeholder="Enter amount"
                disabled={!selectedSource}
              />
              {selectedSource && (
                <div className="text-xs text-gray-500 mt-1">
                  Available: {getMaxTransferAmount().toFixed(2)} GB (Limit: {selectedSource.packageLimit} GB + Topup: {selectedSource.dataTopup || 0} GB - Usage: {selectedSource.currentUsage.toFixed(2)} GB)
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Destination Service
              </label>
              <div className="p-4 bg-gray-50 rounded-2xl border border-gray-200">
                {selectedDestination ? (
                  <div>
                    <div className="font-semibold text-gray-900">{selectedDestination.serviceLine}</div>
                    <div className="text-sm text-gray-600">
                      Current Limit: {selectedDestination.packageLimit} GB
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-400 italic">Select from right panel</div>
                )}
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleTransfer}
              disabled={!selectedSource || !selectedDestination || transferAmount <= 0 || updateFormMutation.isLoading}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-3 rounded-2xl hover:brightness-110 transition-all duration-200 font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateFormMutation.isLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Transferring...
                </>
              ) : (
                <>
                  <i className="fas fa-exchange-alt mr-2"></i>
                  Transfer Data
                </>
              )}
            </button>
          </div>
          {selectedSource && selectedDestination && transferAmount > 0 && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-2xl">
              <div className="text-sm font-semibold text-blue-900 mb-2">Transfer preview</div>
              <div className="text-sm text-blue-800">
                <div>
                  <strong>{selectedSource.serviceLine}</strong>: {selectedSource.packageLimit} GB → {selectedSource.packageLimit - transferAmount} GB
                </div>
                <div>
                  <strong>{selectedDestination.serviceLine}</strong>: {selectedDestination.packageLimit} GB → {selectedDestination.packageLimit + transferAmount} GB
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Two Panels Display */}
      {customerForms.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Panel */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="bg-gray-50 px-6 py-5 border-b border-gray-200">
              <h3 className="text-lg font-black text-gray-900">Source services</h3>
              <p className="text-sm text-gray-600">Select the service to transfer from.</p>
            </div>
            <div className="px-4 pt-4 pb-2 border-b border-gray-200">
              <div className="relative">
                <input
                  type="text"
                  className="w-full rounded-2xl bg-gray-50 px-4 py-2.5 pl-10 text-sm text-gray-900 placeholder-gray-400 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all"
                  placeholder="Search by Service Line or Kit Number..."
                  value={sourceSearch}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (sourceSearchDebounce.current) clearTimeout(sourceSearchDebounce.current);
                    sourceSearchDebounce.current = setTimeout(() => setSourceSearch(val), 300);
                    // Update immediately for responsive UI
                    setSourceSearch(val);
                  }}
                />
                <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
                {sourceSearch && (
                  <button
                    onClick={() => {
                      setSourceSearch('');
                      if (sourceSearchDebounce.current) clearTimeout(sourceSearchDebounce.current);
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <i className="fas fa-times text-sm"></i>
                  </button>
                )}
              </div>
            </div>
            <div className="p-4 max-h-[600px] overflow-y-auto">
              {left.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No services found in this panel
                </div>
              ) : (
                <div className="space-y-3">
                  {left.map((form) => (
                    <div
                      key={form.id}
                      onClick={() => {
                        if (selectedDestination?.id === form.id) {
                          toast.error('Cannot select the same service as source and destination');
                          return;
                        }
                        // Toggle selection: if already selected, deselect it
                        if (selectedSource?.id === form.id) {
                          setSelectedSource(null);
                          setTransferAmount(0);
                        } else {
                          setSelectedSource(form);
                          // Reset transfer amount if it exceeds available data
                          const totalLimit = form.packageLimit + (form.dataTopup || 0);
                          const available = totalLimit - form.currentUsage;
                          const maxAvailable = Math.max(0, available);
                          if (transferAmount > maxAvailable) {
                            setTransferAmount(0);
                          }
                        }
                      }}
                      className={`p-4 rounded-2xl border cursor-pointer transition-all duration-200 ${
                        selectedSource?.id === form.id
                          ? 'border-blue-500 bg-blue-50 shadow-md'
                          : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 mb-1">{form.serviceLine}</div>
                          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-2">
                            <span>{form.customerName}</span>
                            {form.kitNumber ? (
                              <span className="text-xs font-semibold text-gray-900">
                                {form.kitNumber}
                              </span>
                            ) : (
                              <span></span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-gray-500">Package Limit:</span>
                              <span className="font-semibold text-gray-900 ml-1">{form.packageLimit} GB</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Current Usage:</span>
                              <span className="font-semibold text-gray-900 ml-1">{form.currentUsage.toFixed(2)} GB</span>
                            </div>
                            {Number(form.dataTopup || 0) > 0 && (
                              <div className="col-span-2">
                                <span className="text-gray-500">Topup:</span>
                                <span className="font-semibold text-green-600 ml-1">+{form.dataTopup} GB</span>
                              </div>
                            )}
                          </div>
                        </div>
                        {selectedSource?.id === form.id && (
                          <div className="ml-3">
                            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center shadow">
                              <i className="fas fa-check text-white text-sm"></i>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="bg-gray-50 px-6 py-5 border-b border-gray-200">
              <h3 className="text-lg font-black text-gray-900">Destination services</h3>
              <p className="text-sm text-gray-600">Select the service to transfer to.</p>
            </div>
            <div className="px-4 pt-4 pb-2 border-b border-gray-200">
              <div className="relative">
                <input
                  type="text"
                  className="w-full rounded-2xl bg-gray-50 px-4 py-2.5 pl-10 text-sm text-gray-900 placeholder-gray-400 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 transition-all"
                  placeholder="Search by Service Line or Kit Number..."
                  value={destinationSearch}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (destinationSearchDebounce.current) clearTimeout(destinationSearchDebounce.current);
                    destinationSearchDebounce.current = setTimeout(() => setDestinationSearch(val), 300);
                    // Update immediately for responsive UI
                    setDestinationSearch(val);
                  }}
                />
                <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
                {destinationSearch && (
                  <button
                    onClick={() => {
                      setDestinationSearch('');
                      if (destinationSearchDebounce.current) clearTimeout(destinationSearchDebounce.current);
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <i className="fas fa-times text-sm"></i>
                  </button>
                )}
              </div>
            </div>
            <div className="p-4 max-h-[600px] overflow-y-auto">
              {right.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No services found in this panel
                </div>
              ) : (
                <div className="space-y-3">
                  {right.map((form) => (
                    <div
                      key={form.id}
                      onClick={() => {
                        if (selectedSource?.id === form.id) {
                          toast.error('Cannot select the same service as source and destination');
                          return;
                        }
                        // Toggle selection: if already selected, deselect it
                        if (selectedDestination?.id === form.id) {
                          setSelectedDestination(null);
                        } else {
                          setSelectedDestination(form);
                        }
                      }}
                      className={`p-4 rounded-2xl border cursor-pointer transition-all duration-200 ${
                        selectedDestination?.id === form.id
                          ? 'border-emerald-500 bg-emerald-50 shadow-md'
                          : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 mb-1">{form.serviceLine}</div>
                          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-2">
                            <span>{form.customerName}</span>
                            {form.kitNumber ? (
                              <span className="text-xs font-semibold text-gray-900">
                                {form.kitNumber}
                              </span>
                            ) : (
                              <span></span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-gray-500">Package Limit:</span>
                              <span className="font-semibold text-gray-900 ml-1">{form.packageLimit} GB</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Current Usage:</span>
                              <span className="font-semibold text-gray-900 ml-1">{form.currentUsage.toFixed(2)} GB</span>
                            </div>
                            {Number(form.dataTopup || 0) > 0 && (
                              <div className="col-span-2">
                                <span className="text-gray-500">Topup:</span>
                                <span className="font-semibold text-green-600 ml-1">+{form.dataTopup} GB</span>
                              </div>
                            )}
                          </div>
                        </div>
                        {selectedDestination?.id === form.id && (
                          <div className="ml-3">
                            <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center shadow">
                              <i className="fas fa-check text-white text-sm"></i>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {customerIdSearch.length > 0 && customerForms.length === 0 && !isSearching && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12">
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="fas fa-search text-3xl text-gray-400"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">No services found</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              No services found for Customer ID: <strong>{customerIdSearch}</strong>
            </p>
          </div>
        </div>
      )}

      {/* Initial State */}
      {customerIdSearch.length === 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12">
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="fas fa-exchange-alt text-3xl text-blue-600"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Search for Customer</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Enter a Customer ID above to view and transfer data between services
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTransfer;
